import { longDate as dataLonga } from "@historia/shared/dates";
import { themeInfo as infoTema } from "@historia/shared/themes";
import { HINT_PENALTY_MS } from "@historia/shared/validation";
import { emojiRoundResults, wordTime } from "@historia/shared/emoji";
import { timedRoundPoints } from "@historia/shared/validation";
import { formatarTempo } from "@/components/jogos/RondaCronometrada";
import type { FinishedRound as RondaDetalhada } from "@historia/shared/contracts";

/** Short, friendly name from an email. */
function nome(email: string): string {
  const base = email.split("@")[0] ?? email;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/**
 * History card for a finished round — full detail: every word, its emojis, the
 * answer, who guessed it on which try, and the points. Presentational (server).
 */
export function CartaoRonda({
  numero,
  detalhe,
  email,
}: {
  numero: number;
  detalhe: RondaDetalhada;
  email: string | null;
}) {
  // The contract names these `round`/`words`; keep the local names the
  // rest of this component already uses.
  const { round: ronda, words: palavras } = detalhe;
  const dataIso = ronda.criado_em.slice(0, 10);

  // Os pontos da ronda: o mesmo cálculo do servidor, a partir das mesmas
  // palavras. Rondas de antes do cronómetro devolvem [] e a linha não aparece.
  const pontos = timedRoundPoints(
    emojiRoundResults(
      palavras.map((p) => ({
        autor_email: p.autor_email,
        tempo_ms: p.tempo_ms,
        pistas: p.pistas,
        acertou_na: p.acertou_na,
        desistiu: p.desistiu,
      })),
    ),
    "emoji",
  );

  return (
    <article className="rounded-2xl border border-ouro/20 bg-creme-50 p-5">
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="font-titulo text-xl text-tinta-800">Ronda {numero}</h2>
        <span className="text-xs text-tinta-500">{dataLonga(dataIso)}</span>
      </header>

      <ul className="flex flex-col gap-2">
        {palavras.map((p) => {
          const meu = email !== null && p.autor_email === email;
          const correto = p.palpites.find((g) => g.correto);
          return (
            <li
              key={p.id}
              className="rounded-xl bg-creme px-4 py-3 text-sm"
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="text-xl" aria-hidden>
                    {p.emojis}
                  </span>
                  <span className="font-semibold text-tinta-900">{p.palavra ?? "—"}</span>
                </span>
                <span className="text-xs text-tinta-500">
                  {infoTema(p.tema).icon} {infoTema(p.tema).label}
                </span>
              </div>
              <p className="mt-1 text-xs text-tinta-600">
                Por <span className="font-semibold">{meu ? "ti" : nome(p.autor_email)}</span>
                {" · "}
                {p.acertou_na && correto ? (
                  <>
                    {nome(correto.autor_email)} acertou à {p.acertou_na}.ª
                    {/* Palavras de antes do cronómetro não têm tempo — mostra-se
                        o que essa ronda tinha, em vez de um "0:00" inventado. */}
                    {p.tempo_ms !== null && <> em {formatarTempo(wordTime(p))}</>}
                    {p.pistas > 0 && (
                      <>
                        {" "}
                        ({p.pistas} {p.pistas === 1 ? "pista" : "pistas"} · +
                        {p.pistas * Math.round(HINT_PENALTY_MS / 1000)}s)
                      </>
                    )}{" "}
                    🎉
                  </>
                ) : (
                  <>não adivinhada — revelada</>
                )}
              </p>
            </li>
          );
        })}
      </ul>

      {pontos.length > 0 && (
        <ul className="mt-3 flex flex-wrap justify-end gap-3 border-t border-ouro/10 pt-3 text-xs">
          {pontos.map((linha) => (
            <li key={linha.email} className="text-tinta-600">
              <span className="font-semibold text-tinta-800">
                {email === linha.email ? "Tu" : nome(linha.email)}
              </span>{" "}
              {formatarTempo(
                palavras
                  .filter((p) => p.autor_email !== linha.email)
                  .reduce((total, p) => total + wordTime(p), 0),
              )}{" "}
              {linha.points > 0 && (
                <span className="font-semibold text-ouro-600">+{linha.points}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
